import {debounce} from 'throttle-debounce';

type Options = Partial<SearchBoxOptions>;

export interface SearchBoxElement extends HTMLElement {
  searchbox?: SearchBox;
}

export interface SearchBoxOptions {
  activeClass: string;
  contentEl: string;
  debounce: number;
  fieldEl: string;
  scrollLockClass: string;
  selector: string;
  triggerEl: string;
}

export class SearchBox {
  static defaults: SearchBoxOptions = {
    activeClass: 'is-active',
    contentEl: '[data-searchbox-content]',
    debounce: 300,
    fieldEl: '[data-searchbox-input]',
    scrollLockClass: 'mobile-scroll-lock',
    selector: '[data-searchbox]',
    triggerEl: '[data-searchbox-trigger',
  };
  element: SearchBoxElement;
  options: SearchBoxOptions;
  content: HTMLElement;
  focusHandler: EventListener;
  blurHandler: EventListener;
  keyupHandler: EventListener;
  clickHandler: EventListener;

  constructor(element: SearchBoxElement, options: Options = {}) {
    if (!element || element.searchbox) {
      return;
    }

    element.searchbox = this;
    this.element = element;
    this.options = {
      ...SearchBox.defaults,
      ...this.element.dataset,
      ...options,
    };

    const content = this.element.querySelector(
      this.options.contentEl,
    ) as HTMLElement;

    if (!content) {
      return;
    }

    this.content = content;

    this.bind();
  }

  bind() {
    this.focusHandler = this.handleFocus.bind(this);
    this.blurHandler = this.handleBlur.bind(this);
    this.clickHandler = this.handleTriggerClick.bind(this);
    this.keyupHandler = debounce(
      this.options.debounce,
      this.handleKeyUp.bind(this),
    );

    this.element.addEventListener('focus', this.focusHandler, true);
    this.element.addEventListener('blur', this.blurHandler, true);
    this.element.addEventListener('click', this.clickHandler);
    this.element.addEventListener('keyup', this.keyupHandler);
  }

  handleKeyUp() {
    const event = new Event('searchkeyup', {
      bubbles: true,
    });

    this.element.dispatchEvent(event);
  }

  handleFocus(event: Event) {
    this.toggleField(event.target as HTMLElement, true);
  }

  handleBlur(event: Event) {
    this.toggleField(event.target as HTMLElement, false);
  }

  handleTriggerClick(event: Event) {
    const target = event.target as HTMLElement;

    if (!target.closest(this.options.triggerEl)) {
      return;
    }

    event.preventDefault();
    this.content.classList.toggle(this.options.activeClass);
  }

  toggleField(element: HTMLElement, state: boolean) {
    const match = element.closest(this.options.fieldEl);

    if (!match) {
      return;
    }

    this.element.classList.toggle(this.options.activeClass, state);
    document.body.classList.toggle(this.options.scrollLockClass);
  }

  destroy() {
    this.element.removeEventListener('focus', this.focusHandler, true);
    this.element.removeEventListener('blur', this.blurHandler, true);
    this.element.removeEventListener('click', this.clickHandler);
    this.element.removeEventListener('keyup', this.keyupHandler);

    this.element.searchbox = undefined;
  }
}
